import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';

import client from '@/apis/aniftyBackend/client';

type CreationBase = {
  imageURL: string;
  name: string;
  nameJA: string | null;
};

export type GetFavoriteUsersResponse = {
  nextPageToken: string | undefined;
  favoriteUsers: {
    userID: string;
    userName: string | null;
    displayName: string;
    displayNameJA: string | null;
    isCreator: boolean;
    photoURL: string | null;
    collectedCreations: (CreationBase & {
      // for collected creations, creationID may be null (limited collections)
      // but tokenID must be present because they should have been minted
      creationID: number | null;
      tokenID: number;
    })[];
    createdCreations: (CreationBase & {
      // for created creations, creationID must be present
      // but tokenID may be null (not minted yet)
      creationID: number;
      tokenID: number | null;
    })[];
  }[];
};

export const getFavoriteUsers = ({
  userID,
  pageToken,
}: {
  userID: string;
  pageToken: string | undefined;
}): Promise<GetFavoriteUsersResponse> => {
  return client
    .get<GetFavoriteUsersResponse>(`/api/users/${userID}/favorite_users`, {
      params: { page_token: pageToken },
    })
    .then((response) => response.data);
};

const generateQueryKey = (userID: string) => ['favoriteUsers', userID];

type UseGetFavoriteUsersOptions = {
  userID: string;
};

export const useFavoriteUsers = ({
  userID,
}: UseGetFavoriteUsersOptions): UseInfiniteQueryResult<GetFavoriteUsersResponse> => {
  return useInfiniteQuery({
    queryKey: userID ? generateQueryKey(userID) : undefined,
    queryFn: userID
      ? ({ pageParam }) => getFavoriteUsers({ userID, pageToken: pageParam })
      : undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
  });
};

useFavoriteUsers.getKey = generateQueryKey;
